import React from 'react';
import styled from 'styled-components';

import { CentralIntakeBadge } from 'components/centralIntake/CentralIntakeLabels';
import { AFFILIATE } from 'constants/locationTypes';
import { STATE_FILTER_OPTIONS } from 'constants/states';
import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { ClientType } from 'models/flexible/Client';
import type { Group } from 'models/flexible/Group';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminGroupsQueryKeys, adminIndexGroups, archiveGroup } from 'services/api/admin/groups';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import type { AdminGroupIndexFields } from 'services/api/fields';
import { colors } from 'styles/theme/colors';
import DeactivateIcon from 'svg/DeactivateIcon';
import PencilIcon from 'svg/PencilIcon';

const GROUP_INCLUDES = ['groupType', 'activeUsersCount'] as const;

type GroupRow = Group<AdminGroupIndexFields<(typeof GROUP_INCLUDES)[number]>>;

const config: AdminListPageConfig<GroupRow> = {
  title: 'Groups',
  columnStorageKey: 'admin.groups',
  queryKey: adminGroupsQueryKeys.index,
  fetchFn: (params, signal) => adminIndexGroups(params, signal, [...GROUP_INCLUDES]),
  includes: GROUP_INCLUDES,
  fetchParams: {
    defaultSort: 'name asc',
    pageSize: 10,
  },
  searchPlaceholder: 'Search',
  showResultCount: true,

  addButton: {
    label: 'Add Group',
    path: '/admin/groups/new',
    permission: 'adminGroupCreate',
  },

  filters: [
    {
      key: 'clientId',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'groups', 'filter'],
        fetchFn: adminIndexClients,
        params: { 'clientType.not': ClientType.AFFILIATE, sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
      toRequest: (value) => value?.id || undefined,
    },
    {
      key: 'type',
      label: 'Group Type',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminGroupTypes', 'groups', 'filter'],
        fetchFn: indexGroupTypes,
        params: { 'apiName.not': AFFILIATE, sortBy: 'displayName asc' },
      },
      getOptionLabel: (o) => o.displayName,
      getOptionValue: (o) => o.apiName,
      toRequest: (value) => value?.apiName || undefined,
    },
    {
      key: 'state',
      label: 'State',
      type: 'dropdown',
      staticOptions: STATE_FILTER_OPTIONS,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
      toRequest: (value) => value?.id || undefined,
    },
  ],

  columns: [
    {
      id: 'name',
      header: 'Group Name',
      render: (row) => {
        if (row.archived) {
          return <ArchivedText>{row.name}</ArchivedText>;
        }
        return (
          <NameCell>
            <BoldText>{row.name}</BoldText>
            {row.centralIntake && <CentralIntakeBadge />}
          </NameCell>
        );
      },
    },
    {
      id: 'clientName',
      header: 'Client',
      render: (row) => {
        const isParent = !row.client?.leaf;
        return (
          <>
            {row.client?.name}
            {isParent && <ParentLabel> (Parent)</ParentLabel>}
          </>
        );
      },
    },
    {
      id: 'type',
      header: 'Type',
      render: (row) => row.groupType?.displayName ?? '',
    },
    {
      id: 'address',
      header: 'Address',
      sortable: false,
      render: (row) => {
        if (!row.address) return null;
        const { streetAddress, city, state } = row.address;
        return [streetAddress, city, state].filter(Boolean).join(', ');
      },
    },
    {
      id: 'activeUsersCount',
      header: 'Active Users',
      size: 100,
      render: (row) => row.activeUsersCount ?? '',
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminGroupView',
      visible: (row) => !row.archived,
      onClick: (row, { navigate }) => navigate(`/admin/groups/${row.id}/edit`),
    },
    {
      label: 'Archive',
      icon: <DeactivateIcon width={18} height={18} color={colors.red100} />,
      danger: true,
      permission: 'adminGroupDelete',
      visible: (row) => !row.archived,
      onClick: (row, { openModal }) => openModal('archive', row),
    },
  ],

  modals: {
    archive: {
      actionText: 'Archive',
      objType: 'group',
      onConfirm: async (row) => {
        await archiveGroup(row.id);
      },
    },
  },
};

export default function Groups() {
  const state = useAdminListPage(config);
  return <AdminListPageRenderer config={config} state={state} />;
}

const BoldText = styled.span`
  font-weight: 500;
`;

const ArchivedText = styled.span`
  font-weight: 500;
  color: var(--black-25);
`;

const ParentLabel = styled.span`
  font-style: italic;
  margin-left: 4px;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
